import { Check, Info, X } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "~/lib/cn";
import {
  type AppToastVariant,
  useAppToastStore,
} from "~/stores/useAppToastStore";

const ICON = {
  success: Check,
  error: X,
  info: Info,
} as const;

// 成功・情報はブランド色（テーマで Carina の黄系にも追従）、エラーは赤系
const ICON_BG = {
  success: "bg-cyan-500",
  error: "bg-rose-500",
  info: "bg-cyan-500",
} as const;

const VISIBLE_DURATION = 2200;
const EXIT_DURATION = 200;

/**
 * アプリ標準スタイルのトーストを画面中央（モーダルと同じ位置）に表示する。
 * 上部はアプリ内通知、下部はメッセージ入力欄と被るため中央に固定する。
 * __root で 1 度だけマウントする。
 */
export const AppToastViewport = () => {
  const toast = useAppToastStore((s) => s.toast);
  if (!toast) return null;
  // id が変わるたびに ToastCard を再マウントして enter アニメをやり直す
  return (
    <ToastCard key={toast.id} variant={toast.variant} message={toast.message} />
  );
};

const ToastCard = ({
  variant,
  message,
}: {
  variant: AppToastVariant;
  message: string;
}) => {
  const dismiss = useAppToastStore((s) => s.dismiss);
  const [visible, setVisible] = useState(false);
  const Icon = ICON[variant];

  useEffect(() => {
    const enter = requestAnimationFrame(() => setVisible(true));
    const hideTimer = setTimeout(() => setVisible(false), VISIBLE_DURATION);
    const removeTimer = setTimeout(dismiss, VISIBLE_DURATION + EXIT_DURATION);
    return () => {
      cancelAnimationFrame(enter);
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
    };
  }, [dismiss]);

  return (
    <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center px-6">
      <div
        className={cn(
          "flex max-w-sm items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-lg ring-1 ring-black/5 transition-all duration-200",
          visible ? "scale-100 opacity-100" : "scale-95 opacity-0",
        )}
      >
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-full",
            ICON_BG[variant],
          )}
        >
          <Icon className="size-4 text-white" strokeWidth={2.5} />
        </span>
        <p className="min-w-0 break-words text-sm text-zinc-900">{message}</p>
      </div>
    </div>
  );
};
